<script setup>
// SiteUp (#53): indicador AO VIVO de que a IA está compondo a resposta.
// Escuta o evento 'captainProcessing' (broadcast do AgentRunnerService via
// ActionCable) e mostra: ícone girando + tempo decorrido + tools que a IA está
// usando em tempo real. Some sozinho no 'done' (ou por segurança em 2min).
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { labelForTool } from 'shared/helpers/captainToolLabels';
import BrasaMascot from 'dashboard/components-next/onboarding/BrasaMascot.vue';

const props = defineProps({
  conversationId: { type: [Number, String], default: null },
});

const { t } = useI18n();

const processing = ref(false);
const tools = ref([]);
const startedAt = ref(0);
const elapsed = ref(0);
let ticker = null;
let safety = null;

// Rótulos PT-BR das tools: fonte única em shared/helpers/captainToolLabels
// (mesmo mapa usado no rodapé persistente sob a bolha em Message.vue).
const prettyTools = computed(() => [...new Set(tools.value)].map(labelForTool));
const toolsLabel = computed(() =>
  prettyTools.value.join(t('CONVERSATION.CAPTAIN_PROCESSING.TOOL_SEPARATOR'))
);

const elapsedLabel = computed(() => {
  const s = elapsed.value;
  if (s < 60) {
    return t('CONVERSATION.CAPTAIN_PROCESSING.ELAPSED_SECONDS', { n: s });
  }
  return t('CONVERSATION.CAPTAIN_PROCESSING.ELAPSED_MINUTES', {
    m: Math.floor(s / 60),
    s: s % 60,
  });
});

// Sem tool ainda → pensando; com ferramenta → "usando ferramentas"
// (as ferramentas em si aparecem na mesma linha).
const stateLabel = computed(() =>
  prettyTools.value.length
    ? t('CONVERSATION.CAPTAIN_PROCESSING.USING_TOOLS')
    : t('CONVERSATION.CAPTAIN_PROCESSING.THINKING')
);

// SITEUP V2 (F1.3): estado do Thinking Orb acompanha a fase real —
// working = gerando resposta · solving = executando ferramenta.
const brasaState = computed(() =>
  prettyTools.value.length ? 'inspect' : 'hover'
);

const stop = () => {
  processing.value = false;
  if (ticker) clearInterval(ticker);
  if (safety) clearTimeout(safety);
  ticker = null;
  safety = null;
};

const start = () => {
  startedAt.value = Date.now();
  elapsed.value = 0;
  tools.value = [];
  processing.value = true;
  if (ticker) clearInterval(ticker);
  ticker = setInterval(() => {
    elapsed.value = Math.floor((Date.now() - startedAt.value) / 1000);
  }, 1000);
  if (safety) clearTimeout(safety);
  safety = setTimeout(stop, 120000);
};

// SiteUp (#82): tempo mínimo de exibição — quando o 'done' chega logo após o
// 'started' (geração rápida), o indicador sumia rápido demais pra ser percebido.
// Garante pelo menos MIN_VISIBLE_MS de exibição. O cronômetro conta desde o 1º
// 'started' (não reinicia a cada evento), abrangendo o debounce + a geração.
const MIN_VISIBLE_MS = 4000;

const onProcessing = data => {
  if (!data || String(data.conversation_id) !== String(props.conversationId)) {
    return;
  }
  if (data.status === 'done') {
    const visibleFor = Date.now() - startedAt.value;
    if (processing.value && visibleFor < MIN_VISIBLE_MS) {
      if (safety) clearTimeout(safety);
      safety = setTimeout(stop, MIN_VISIBLE_MS - visibleFor);
    } else {
      stop();
    }
    return;
  }
  if (!processing.value) start();
  if (data.tools && data.tools.length) tools.value = data.tools;
};

watch(() => props.conversationId, stop);
onMounted(() => emitter.on('captainProcessing', onProcessing));
onBeforeUnmount(() => {
  emitter.off('captainProcessing', onProcessing);
  stop();
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <!--
    Mesma coluna da bolha outgoing (Message.vue): justify-end + grid
    1fr/24px + ml-8. Orb 20px INLINE — size 64 + flex-col vira tile órfão.
  -->
  <div v-if="processing" class="flex w-full mb-2 justify-end">
    <div class="grid w-full grid-cols-[1fr_24px] gap-x-2">
      <div class="flex min-w-0 items-center justify-end ltr:ml-8 rtl:mr-8">
        <div
          role="status"
          aria-live="polite"
          class="inline-flex max-w-lg min-w-0 items-center gap-2 text-sm right-bubble rounded-[var(--workspace-radius,20px)] px-4 py-3 ltr:rounded-br-[5px] rtl:rounded-bl-[5px] bg-n-solid-2 text-n-slate-12 ltr:border-l-[3px] rtl:border-r-[3px] ltr:border-l-n-iris-9 rtl:border-r-n-iris-9"
        >
          <BrasaMascot :state="brasaState" :size="20" class="shrink-0" />
          <span class="font-medium min-w-0 truncate">{{ stateLabel }}</span>
          <span class="text-n-slate-11 tabular-nums shrink-0">{{
            elapsedLabel
          }}</span>
          <span
            v-if="prettyTools.length"
            class="inline-flex items-center gap-1 min-w-0 text-n-slate-10"
          >
            <span class="h-3 w-px shrink-0 bg-n-slate-8" aria-hidden="true" />
            <span class="i-ri-wrench-line size-3 shrink-0" aria-hidden="true" />
            <span class="truncate">{{ toolsLabel }}</span>
          </span>
        </div>
      </div>
      <div aria-hidden="true" />
    </div>
  </div>
</template>
